import {
  applyTextCase,
  effectiveThemeForSlide,
  fetchUrlAsDataUrl,
  layerRenderStyle,
  resolveFittedTextStyle,
  sortedLayers,
  surfPreviewScrimStyle,
  type SurfLayer,
  type SurfSlide,
  type SurfStyle,
} from "@vida/surf-core";

const STAGE_W = 1280;
const STAGE_H = 720;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Caricamento immagine raster fallito"));
    img.src = src;
  });
}

function wrapLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidthPx: number,
): string[] {
  const paragraphs = text.split("\n");
  const lines: string[] = [];
  for (const para of paragraphs) {
    const words = para.trim() ? para.split(/\s+/) : [""];
    let line = "";
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (ctx.measureText(candidate).width <= maxWidthPx || !line) {
        line = candidate;
      } else {
        lines.push(line);
        line = word;
      }
    }
    lines.push(line);
  }
  return lines;
}

function drawTextOnCanvas(
  ctx: CanvasRenderingContext2D,
  layer: SurfLayer,
  theme: SurfStyle,
  stageH: number,
): void {
  const ts = resolveFittedTextStyle(layer, theme);
  const render = layerRenderStyle(layer, theme, stageH);
  const text = applyTextCase(layer.content?.trim() || "", ts.case);
  if (!text) return;

  const x = layer.x * STAGE_W;
  const y = layer.y * STAGE_H;
  const w = layer.w * STAGE_W;
  const h = layer.h * STAGE_H;

  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();

  ctx.fillStyle = render.fillText;
  ctx.font = `${render.fontStyle} ${render.fontSize}px ${render.fontFamily}`;
  ctx.textBaseline = "top";
  ctx.textAlign = render.align;

  const lineHeightPx = render.fontSize * render.lineHeight;
  const lines = wrapLines(ctx, text, w);
  let cursorY = y;

  for (let i = 0; i < lines.length; i += 1) {
    if (cursorY + lineHeightPx > y + h) break;
    const line = lines[i]!;
    let drawX = x;
    if (render.align === "center") drawX = x + w / 2;
    else if (render.align === "right") drawX = x + w;
    ctx.fillText(line, drawX, cursorY);
    cursorY += lineHeightPx;
  }

  ctx.restore();
}

export type RasterizeSurfSlideInput = {
  slide: SurfSlide;
  theme: SurfStyle;
  /** Presigned URL or data URL for slide background */
  backgroundUrl?: string | null;
};

/** Offscreen composite (background + text) for vision autofit — matches export resolver. */
export async function rasterizeSurfSlide(input: RasterizeSurfSlideInput): Promise<string> {
  const canvas = document.createElement("canvas");
  canvas.width = STAGE_W;
  canvas.height = STAGE_H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D non disponibile");

  const slideTheme = effectiveThemeForSlide(input.theme, input.slide);

  if (input.backgroundUrl) {
    const src = input.backgroundUrl.startsWith("data:")
      ? input.backgroundUrl
      : await fetchUrlAsDataUrl(input.backgroundUrl);
    const img = await loadImage(src);
    ctx.drawImage(img, 0, 0, STAGE_W, STAGE_H);
  } else {
    const manual = input.slide.background.solidColor?.trim();
    ctx.fillStyle =
      manual && /^#[0-9A-Fa-f]{6}$/i.test(manual)
        ? manual.toUpperCase()
        : input.theme.palette.surface;
    ctx.fillRect(0, 0, STAGE_W, STAGE_H);
  }

  const scrim = surfPreviewScrimStyle(slideTheme);
  if (scrim?.backgroundColor) {
    ctx.save();
    ctx.globalAlpha = typeof scrim.opacity === "number" ? scrim.opacity : 0.35;
    ctx.fillStyle = String(scrim.backgroundColor);
    ctx.fillRect(0, 0, STAGE_W, STAGE_H);
    ctx.restore();
  }

  for (const layer of sortedLayers(input.slide)) {
    if (layer.type === "text") {
      drawTextOnCanvas(ctx, layer, slideTheme, STAGE_H);
    }
  }

  return canvas.toDataURL("image/jpeg", 0.85);
}
